import { getDisplayOrderNumber, OrderStatus } from '../types/order';
import { asArray, asNumber, asOptionalString, asRecord, asString } from './common';
import { OrderDto, normalizeOrder, normalizeOrderStatus } from './order';

export interface DashboardDataDto {
  stats: {
    totalOrders?: number;
    activeOrders?: number;
    monthlyRevenue?: number;
    monthlyProfit?: number;
    monthlyCosts?: number;
  };
  statusCounts: Record<string, number>;
  recentOrders: OrderDto[];
  workQueue: OrderDto[];
  operationalBuckets: DashboardBucketDto[];
  sideBuckets: DashboardBucketDto[];
  upcomingDeadlines: OrderDto[];
  upcomingDeadlineCount: number;
  upcomingDeadlineDays: DashboardDeadlineDayDto[];
  overdueCount: number;
  customerPayments?: DashboardCustomerPaymentsDto;
  lowMarginOrders?: OrderDto[];
}

export interface DashboardBucketDto {
  key: string;
  statuses: OrderStatus[];
  count: number;
}

export interface DashboardDeadlineDayDto {
  date: string;
  count: number;
}

export interface DashboardCurrencyAmountDto {
  currency: string;
  amount: number;
}

export interface DashboardCustomerPaymentsDto {
  horizonDays: number;
  expectedByCurrency: DashboardCurrencyAmountDto[];
  overdueByCurrency: DashboardCurrencyAmountDto[];
  upcoming: OrderDto[];
  overdue: OrderDto[];
}

export interface MonthlyProfitReportDto {
  summary: {
    totalOrders: number;
    totalRevenue: number;
    totalCosts: number;
    totalMargin: number;
    averageMarginPercent: number;
    ordersByStatus: Record<string, number>;
    costsByCategory: Record<string, number>;
  };
  orders: OrderDto[];
}

export interface GoodsInTransitOrderDto {
  id: string;
  orderNumber: string;
  displayOrderNumber: string;
  customerName: string;
  supplierName: string;
  expectedDelivery?: string;
  deliveryLocation?: string;
  revenue: number;
  currency: string;
  products: Array<{
    id: string;
    name: string;
    quantity: number;
    unitPrice?: number;
    totalPrice?: number;
    currency?: string;
    cnCode?: string;
    grossWeight?: number;
  }>;
}

export interface GoodsInTransitReportDto {
  summary: {
    totalOrders: number;
    totalProducts: number;
    totalValue: number;
    totalValueCurrency: string;
    totalWeight: number;
    byDestination: Record<string, { count: number; value: number }>;
  };
  orders: GoodsInTransitOrderDto[];
}

export interface CostsPaymentStatusReportDto {
  summary: {
    totalCount: number;
    paidCount: number;
    unpaidCount: number;
    totalAmount: number;
    paidAmount: number;
    unpaidAmount: number;
  };
  byCategory: Array<{
    category: string;
    count: number;
    totalAmount: number;
    paidAmount: number;
    unpaidAmount: number;
  }>;
  unpaidCosts: Array<{
    id: string;
    orderNumber: string;
    displayOrderNumber: string;
    category: string;
    amount: number;
  }>;
}

export interface SalesByUserDto {
  userId: string;
  userName: string;
  orderCount: number;
  totalRevenue: number;
  totalCost: number;
  totalMargin: number;
  avgMarginPercent: number;
}

export interface AtRiskOrdersReportDto {
  threshold: number;
  orders: OrderDto[];
}

export interface SettlementsReportDto {
  summary: {
    avgDeviation: number;
    negativeDeviationPercent: number;
  };
  orders: Array<{
    id: string;
    orderNumber: string;
    displayOrderNumber: string;
    customerName: string;
    plannedMarginPercent: number;
    settledMarginPercent: number;
    deviationPercent: number;
  }>;
}

export function normalizeDashboardData(raw: unknown): DashboardDataDto {
  const record = asRecord(raw);
  const stats = asRecord(record?.stats);

  return {
    stats: {
      totalOrders: asOptionalNumber(stats?.totalOrders),
      activeOrders: asOptionalNumber(stats?.activeOrders),
      monthlyRevenue: asOptionalNumber(stats?.monthlyRevenue),
      monthlyProfit: asOptionalNumber(stats?.monthlyProfit),
      monthlyCosts: asOptionalNumber(stats?.monthlyCosts),
    },
    statusCounts: normalizeNumberDictionary(record?.statusCounts),
    recentOrders: asArray(record?.recentOrders, (item, index) => normalizeOrder(item, `dashboard.recentOrders[${index}]`)),
    workQueue: asArray(record?.workQueue, (item, index) => normalizeOrder(item, `dashboard.workQueue[${index}]`)),
    operationalBuckets: asArray(record?.operationalBuckets, (item, index) => normalizeDashboardBucket(item, `dashboard.operationalBuckets[${index}]`)),
    sideBuckets: asArray(record?.sideBuckets, (item, index) => normalizeDashboardBucket(item, `dashboard.sideBuckets[${index}]`)),
    upcomingDeadlines: asArray(record?.upcomingDeadlines, (item, index) => normalizeOrder(item, `dashboard.upcomingDeadlines[${index}]`)),
    upcomingDeadlineCount: asNumber(record?.upcomingDeadlineCount),
    upcomingDeadlineDays: asArray(record?.upcomingDeadlineDays, (item, index) => normalizeDashboardDeadlineDay(item, `dashboard.upcomingDeadlineDays[${index}]`)),
    overdueCount: asNumber(record?.overdueCount),
    customerPayments: record?.customerPayments ? normalizeDashboardCustomerPayments(record.customerPayments) : undefined,
    lowMarginOrders: Array.isArray(record?.lowMarginOrders)
      ? asArray(record.lowMarginOrders, (item, index) => normalizeOrder(item, `dashboard.lowMarginOrders[${index}]`))
      : undefined,
  };
}

function asOptionalNumber(value: unknown): number | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  return asNumber(value);
}

function normalizeDashboardBucket(raw: unknown, entity: string): DashboardBucketDto {
  const record = asRecord(raw);

  return {
    key: asString(record?.key),
    statuses: asArray(record?.statuses, (status, index) => normalizeOrderStatus(status, `${entity}.statuses[${index}]`)),
    count: asNumber(record?.count),
  };
}

function normalizeDashboardDeadlineDay(raw: unknown, entity: string): DashboardDeadlineDayDto {
  const record = asRecord(raw);

  return {
    date: asString(record?.date, entity),
    count: asNumber(record?.count),
  };
}

function normalizeCurrencyAmount(raw: unknown): DashboardCurrencyAmountDto {
  const record = asRecord(raw);

  return {
    currency: asString(record?.currency, 'PLN'),
    amount: asNumber(record?.amount),
  };
}

function normalizeDashboardCustomerPayments(raw: unknown): DashboardCustomerPaymentsDto {
  const record = asRecord(raw);

  return {
    horizonDays: asNumber(record?.horizonDays, 14),
    expectedByCurrency: asArray(record?.expectedByCurrency, normalizeCurrencyAmount),
    overdueByCurrency: asArray(record?.overdueByCurrency, normalizeCurrencyAmount),
    upcoming: asArray(record?.upcoming, (item, index) => normalizeOrder(item, `dashboard.customerPayments.upcoming[${index}]`)),
    overdue: asArray(record?.overdue, (item, index) => normalizeOrder(item, `dashboard.customerPayments.overdue[${index}]`)),
  };
}

export function normalizeMonthlyProfitReport(raw: unknown): MonthlyProfitReportDto {
  const record = asRecord(raw);
  const summary = asRecord(record?.summary);

  return {
    summary: {
      totalOrders: asNumber(summary?.totalOrders),
      totalRevenue: asNumber(summary?.totalRevenue),
      totalCosts: asNumber(summary?.totalCosts),
      totalMargin: asNumber(summary?.totalMargin),
      averageMarginPercent: asNumber(summary?.averageMarginPercent),
      ordersByStatus: normalizeNumberDictionary(summary?.ordersByStatus),
      costsByCategory: normalizeNumberDictionary(summary?.costsByCategory),
    },
    orders: asArray(record?.orders, (item, index) => {
      const orderRecord = asRecord(item);
      const order = normalizeOrder(item, `monthlyProfit.orders[${index}]`);
      const backendDisplayOrderNumber = asOptionalString(orderRecord?.displayOrderNumber);
      if (backendDisplayOrderNumber) {
        order.displayOrderNumber = backendDisplayOrderNumber;
      }
      return order;
    }),
  };
}

export function normalizeGoodsInTransitReport(raw: unknown): GoodsInTransitReportDto {
  const record = asRecord(raw);
  const summary = asRecord(record?.summary);

  return {
    summary: {
      totalOrders: asNumber(summary?.totalOrders),
      totalProducts: asNumber(summary?.totalProducts),
      totalValue: asNumber(summary?.totalValue),
      totalValueCurrency: asString(summary?.totalValueCurrency, 'PLN'),
      totalWeight: asNumber(summary?.totalWeight),
      byDestination: normalizeDestinationSummary(summary?.byDestination),
    },
    orders: asArray(record?.orders, (item, index) => {
      const order = asRecord(item);
      const customer = asRecord(order?.customer);
      const supplier = asRecord(order?.supplier);

      return {
        id: asString(order?.id),
        orderNumber: asString(order?.orderNumber),
        displayOrderNumber: normalizeReportDisplayOrderNumber(order),
        customerName: asString(customer?.name),
        supplierName: asString(supplier?.name),
        expectedDelivery: order?.expectedDelivery ? asString(order?.expectedDelivery) : undefined,
        deliveryLocation: order?.deliveryLocation ? asString(order?.deliveryLocation) : undefined,
        revenue: asNumber(order?.revenue),
        currency: asString(order?.currency, 'PLN'),
        products: asArray(order?.products, (product) => {
          const productRecord = asRecord(product);

          return {
            id: asString(productRecord?.id),
            name: asString(productRecord?.name),
            quantity: asNumber(productRecord?.quantity),
            unitPrice: productRecord?.unitPrice == null ? undefined : asNumber(productRecord?.unitPrice),
            totalPrice: productRecord?.totalPrice == null ? undefined : asNumber(productRecord?.totalPrice),
            currency: productRecord?.currency ? asString(productRecord?.currency) : undefined,
            cnCode: productRecord?.cnCode ? asString(productRecord?.cnCode) : undefined,
            grossWeight: productRecord?.grossWeight == null ? undefined : asNumber(productRecord?.grossWeight),
          };
        }),
      };
    }),
  };
}

export function normalizeCostsPaymentStatusReport(raw: unknown): CostsPaymentStatusReportDto {
  const record = asRecord(raw);
  const summary = asRecord(record?.summary);

  return {
    summary: {
      totalCount: asNumber(summary?.totalCount),
      paidCount: asNumber(summary?.paidCount),
      unpaidCount: asNumber(summary?.unpaidCount),
      totalAmount: asNumber(summary?.totalAmount),
      paidAmount: asNumber(summary?.paidAmount),
      unpaidAmount: asNumber(summary?.unpaidAmount),
    },
    byCategory: asArray(record?.byCategory, (item) => {
      const category = asRecord(item);

      return {
        category: asString(category?.category),
        count: asNumber(category?.count),
        totalAmount: asNumber(category?.totalAmount),
        paidAmount: asNumber(category?.paidAmount),
        unpaidAmount: asNumber(category?.unpaidAmount),
      };
    }),
    unpaidCosts: asArray(record?.unpaidCosts, (item) => {
      const cost = asRecord(item);

      return {
        id: asString(cost?.id),
        orderNumber: asString(cost?.orderNumber),
        displayOrderNumber: normalizeReportDisplayOrderNumber(cost),
        category: asString(cost?.category),
        amount: asNumber(cost?.amount),
      };
    }),
  };
}

export function normalizeSalesByUser(raw: unknown): SalesByUserDto[] {
  return asArray(raw, (item) => {
    const record = asRecord(item);

    return {
      userId: asString(record?.userId),
      userName: asString(record?.userName),
      orderCount: asNumber(record?.orderCount),
      totalRevenue: asNumber(record?.totalRevenue),
      totalCost: asNumber(record?.totalCost),
      totalMargin: asNumber(record?.totalMargin),
      avgMarginPercent: asNumber(record?.avgMarginPercent),
    };
  });
}

export function normalizeAtRiskOrdersReport(raw: unknown): AtRiskOrdersReportDto {
  const record = asRecord(raw);

  return {
    threshold: asNumber(record?.threshold),
    orders: asArray(record?.orders, (item, index) => {
      const orderRecord = asRecord(item);
      const order = normalizeOrder(item, `atRisk.orders[${index}]`);
      const backendDisplayOrderNumber = asOptionalString(orderRecord?.displayOrderNumber);
      const flatCustomerName = asOptionalString(orderRecord?.customerName);
      if (backendDisplayOrderNumber) {
        order.displayOrderNumber = backendDisplayOrderNumber;
      }
      if (flatCustomerName) {
        order.customerName = flatCustomerName;
      }
      return order;
    }),
  };
}

export function normalizeSettlementsReport(raw: unknown): SettlementsReportDto {
  const record = asRecord(raw);
  const summary = asRecord(record?.summary);

  return {
    summary: {
      avgDeviation: asNumber(summary?.avgDeviation),
      negativeDeviationPercent: asNumber(summary?.negativeDeviationPercent),
    },
    orders: asArray(record?.orders, (item) => {
      const order = asRecord(item);
      const customer = asRecord(order?.customer);

      return {
        id: asString(order?.id),
        orderNumber: asString(order?.orderNumber),
        displayOrderNumber: normalizeReportDisplayOrderNumber(order),
        customerName: asString(customer?.name),
        plannedMarginPercent: asNumber(order?.plannedMarginPercent),
        settledMarginPercent: asNumber(order?.settledMarginPercent),
        deviationPercent: asNumber(order?.deviationPercent ?? order?.marginDeviation),
      };
    }),
  };
}

function normalizeReportDisplayOrderNumber(record: ReturnType<typeof asRecord>): string {
  const orderNumber = asString(record?.orderNumber);

  return asOptionalString(record?.displayOrderNumber)
    || getDisplayOrderNumber({
      orderNumber,
      manualOrderNumber: asOptionalString(record?.manualOrderNumber) ?? null,
      useManualNumbering: record?.useManualNumbering === true,
    });
}

function normalizeDestinationSummary(raw: unknown): Record<string, { count: number; value: number }> {
  const record = asRecord(raw);

  if (!record) {
    return {};
  }

  return Object.entries(record).reduce<Record<string, { count: number; value: number }>>((acc, [key, value]) => {
    const destination = asRecord(value);
    acc[key] = {
      count: asNumber(destination?.count),
      value: asNumber(destination?.value),
    };
    return acc;
  }, {});
}

function normalizeNumberDictionary(raw: unknown): Record<string, number> {
  const record = asRecord(raw);

  if (!record) {
    return {};
  }

  return Object.entries(record).reduce<Record<string, number>>((acc, [key, value]) => {
    acc[key] = asNumber(value);
    return acc;
  }, {});
}

export function getOrderStatusLabel(status: OrderStatus): OrderStatus {
  return status;
}
